import React, { useState, useRef, useLayoutEffect } from "react";
import smoothscroll from "smoothscroll-polyfill";
import "./LuPSMA.scss";
import DisclaimerModal from "../DisclaimerModal";
import NumberInput from "../NumberInput";
import BooleanInput from "../BooleanInput";
import OverallSurvivabilityChart from "../OverallSurvivabilityChart";
import PSAProgressionFreeChart from "../PSAProgressionFreeChart";
import PSAResponseProbability from "../PSAResponseProbability";
import SurvivalProbability from "../SurvivalProbability";
import generateReport from "../../utilities/generateLuPSMAReport";
import {
  calculateSurvivability,
  calculatePSAProgressionFreeSurvival,
  calculateLogisticPSA50PercentDecline,
  calculateLogisticPSA50PercentDeclilneNomogram,
} from "../../utilities/predictionModel";

const INITIAL_INPUTS = {
  timeSinceDiagnosis: 0,
  previousChemotheraphy: false,
  hemoglobin: 0,
  suvMean: 0,
  metastasesCount: false, // false if < 20, true of >= 20
  pelvicLymphNodes: false,
  boneMetastases: false,
  liverMetastases: false,
};

const INITIAL_PREDICTIONS = {
  survivability12Months: 0,
  survivability18Months: 0,
  psaProgressionFreeSurvival3Months: 0,
  psaProgressionFreeSurvival6Months: 0,
  logisticPSA50PercentDecline: 0,
};

const INITIAL_CHART_IMAGES = {
  survivabilityChart: null,
  psaProgressionFreeChart: null,
};

export default function LuPSMA() {
  smoothscroll.polyfill();
  const [inputs, setInputs] = useState({ ...INITIAL_INPUTS });
  const [predictions, setPredictions] = useState({ ...INITIAL_PREDICTIONS });
  const [logisticPSA50PrecentDeclineLowRisk, setLogisticPSA50PrecentDeclineLowRisk] = useState(false);
  const [calculateButtonClicks, setCalculateButtonClicks] = useState(0);
  const [isReportOpening, setIsReportOpening] = useState(false);
  const chartImages = useRef(INITIAL_CHART_IMAGES);
  const formSectionRef = useRef(null);
  const resultsSectionRef = useRef(null);

  const isInitialState = JSON.stringify(inputs) === JSON.stringify(INITIAL_INPUTS);

  const userPlatform = () => {
    const platform = window.navigator.platform;
    const macosPlatforms = ["Macintosh", "MacIntel", "MacPPC", "Mac68K"];
    const windowsPlatforms = ["Win32", "Win64", "Windows", "WinCE"];

    if (macosPlatforms.indexOf(platform) !== -1) {
      return "Mac OS";
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      return "Windows";
    }

    return null;
  };

  const onInputChange = (id, value) => {
    setInputs({
      ...inputs,
      [id]: value,
    });
  };

  const onChartChange = async (id, chartImage) => {
    chartImages.current[id] = chartImage;
  };

  useLayoutEffect(() => {
    if (calculateButtonClicks > 0) {
      resultsSectionRef.current.scrollIntoView({ behavior: "smooth" });
    } else {
      formSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [calculateButtonClicks]);

  const onNextPatientClick = () => {
    setInputs({ ...INITIAL_INPUTS });
    setPredictions({ ...INITIAL_PREDICTIONS });
    setIsReportOpening(false);
    setCalculateButtonClicks(0);
  };

  const onCalculateClick = () => {
    setPredictions({
      ...calculateSurvivability(inputs),
      ...calculatePSAProgressionFreeSurvival(inputs),
      ...calculateLogisticPSA50PercentDecline(inputs),
    });
    setLogisticPSA50PrecentDeclineLowRisk(calculateLogisticPSA50PercentDeclilneNomogram(inputs).lowRisk);
    setCalculateButtonClicks(calculateButtonClicks + 1);
  };

  const onOpenPDFReportClick = () => {
    setIsReportOpening(true);
    onCalculateClick();

    // Introduce a delay so the canvas is re-rendered properly
    setTimeout(() => {
      const pdfReport = generateReport(
        inputs,
        {
          ...calculateSurvivability(inputs),
          ...calculatePSAProgressionFreeSurvival(inputs),
          ...calculateLogisticPSA50PercentDecline(inputs),
        },
        chartImages.current
      );

      const popUpWindow = window.open("", "_blank");
      if (!popUpWindow) {
        alert(
          'Please allow pop-up windows from this website to display PDF reports in your browser or use the "Download PDF report" option.'
        );
      } else {
        pdfReport.open({}, popUpWindow);
      }

      setIsReportOpening(false);
    }, 1100);
  };

  const isCalculateButtonClicked = calculateButtonClicks > 0;

  return (
    <div className="LuPSMA">
      <DisclaimerModal />
      <div className="container-lg">
        <div className="alert alert-warning" role="alert">
          This risk calculator is a research product and not validated for clinical use.
        </div>
        {/* Title */}
        <section className="row">
          <div className="col">
            <h2 className="text-center">LuPSMA Prognostic Model</h2>
          </div>
        </section>
        <hr />
        {/* Intro */}
        <section className="row">
          <div className="col">
            <p className="text-justify">
              Researchers from University of California, Los Angeles and other five institutions across Europe,
              Australia and the United States of America have developed a prognostic tool, commonly referred to as a
              nomogram, designed to estimate the risk of progression-free and overall survival for patients treated with
              177Lu&#8209;PSMA radionuclide treatment (LuPSMA). The nomogram may assist physicians when considering
              treating patients with LuPSMA radionuclide treatment but cannot substitute clinical judgment and therefore
              should not dictate clinical decision.
            </p>
            <p className="text-justify">
              This prognostic tool has been developed using information from hundreds of men with metastatic
              castration-resistant prostate cancer. The LuPSMA nomogram can calculate individual patient risk based on
              clinical and imaging characteristics before treatment initiation.
            </p>
            <p className="text-justify">
              After risk calculation, the nomogram generates automatically a report which can be saved in PDF format and
              printed out. The report provides detailed information of the calculated risk along with explanation of the
              input of each parameter on patient’s outcome.
            </p>
            <p className="text-justify">
              There are two ways for using the LuPSMA prediction tool:
              <ul>
                {userPlatform() === "Mac OS" && (
                  <li>
                    A <b>desktop version</b>, which can be downloaded <a href="dist/Nomogram-1.4.4.dmg">here</a>.
                  </li>
                )}
                {userPlatform() === "Windows" && (
                  <li>
                    A <b>desktop version</b>, which can be downloaded <a href="dist/Nomogram Setup 1.4.4.exe">here</a>.
                  </li>
                )}
                {userPlatform() === null && (
                  <li>
                    A <b>desktop version</b>, which is available for <a href="dist/Nomogram Setup 1.4.4.exe">Windows</a>{" "}
                    and <a href="dist/Nomogram-1.4.4.dmg">macOS</a> operating systems.
                  </li>
                )}
                <li>
                  A <b>web version</b>, which is ready for use as an online risk calculator underneath.
                </li>
              </ul>
            </p>
          </div>
        </section>
        <hr />
        {/* Form inputs */}
        <section ref={formSectionRef}>
          <div className="row">
            <div className="col">
              <h3 className="text-center">Risk Calculator</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <form className="form-col">
                <h5>Medical History</h5>
                <NumberInput
                  id="timeSinceDiagnosis"
                  label="Time since diagnosis"
                  unitLabel="years"
                  value={inputs.timeSinceDiagnosis}
                  onChange={onInputChange}
                  minValue={0}
                  maxValue={30}
                  step={1}
                />
                <BooleanInput
                  id="previousChemotheraphy"
                  label="Previous chemotherapy"
                  value={inputs.previousChemotheraphy}
                  onChange={onInputChange}
                />

                <h5 className="laboratory-values">Laboratory Values</h5>
                <NumberInput
                  id="hemoglobin"
                  label="Hemoglobin"
                  unitLabel="g/dl"
                  value={inputs.hemoglobin}
                  onChange={onInputChange}
                  minValue={0}
                  maxValue={15}
                  step={1}
                />
              </form>
            </div>
            <div className="col-md-6">
              <form className="form-col">
                <h5>PSMA PET/CT Parameters</h5>
                <NumberInput
                  id="suvMean"
                  label="Tumor SUVmean"
                  value={inputs.suvMean}
                  onChange={onInputChange}
                  minValue={0}
                  maxValue={50}
                  step={1}
                />
                <BooleanInput
                  id="metastasesCount"
                  label="Number of metastases"
                  value={inputs.metastasesCount}
                  onChange={onInputChange}
                  falseLabel={"\u00A0\u00A0\u003C\u200920\u00A0\u00A0"}
                  trueLabel={"\u00A0\u00A0\u2265\u00A020\u00A0\u00A0"}
                />
                <BooleanInput
                  id="pelvicLymphNodes"
                  label="Pelvic lymph nodes"
                  value={inputs.pelvicLymphNodes}
                  onChange={onInputChange}
                />
                <BooleanInput
                  id="boneMetastases"
                  label="Bone metastases"
                  value={inputs.boneMetastases}
                  onChange={onInputChange}
                />
                <BooleanInput
                  id="liverMetastases"
                  label="Liver metastases"
                  value={inputs.liverMetastases}
                  onChange={onInputChange}
                />
              </form>
            </div>
          </div>
        </section>
        {/* Calculate button */}
        <section className="row">
          <div className="col">
            <button
              type="button"
              className="btn btn-outline-primary btn-block"
              onClick={onCalculateClick}
              disabled={isInitialState}
            >
              Calculate
            </button>
          </div>
        </section>
        {/* Results */}
        {isCalculateButtonClicked && (
          <React.Fragment>
            <section id="resultsSection" ref={resultsSectionRef}>
              <div className="row">
                <div className="col-md-6">
                  <div className="row">
                    <div className="col">
                      <h4 className="text-center">Overall Survival</h4>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4 offset-2">
                      <SurvivalProbability value={predictions.survivability12Months} label="12 months" />
                    </div>
                    <div className="col-4">
                      <SurvivalProbability value={predictions.survivability18Months} label="18 months" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <OverallSurvivabilityChart predictions={predictions} onChartChange={onChartChange} />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="row">
                    <div className="col">
                      <h4 className="text-center">PSA Progression-free Survival</h4>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-4 offset-2">
                      <SurvivalProbability value={predictions.psaProgressionFreeSurvival3Months} label="3 months" />
                    </div>
                    <div className="col-4">
                      <SurvivalProbability value={predictions.psaProgressionFreeSurvival6Months} label="6 months" />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <PSAProgressionFreeChart predictions={predictions} onChartChange={onChartChange} />
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <section id="PSAResponseProbabilitySection">
              <PSAResponseProbability predictions={predictions} nonResponser={!logisticPSA50PrecentDeclineLowRisk} />
            </section>
            {/* Menu */}
            <section id="menuSection" className="row">
              <div className="col-md">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-block menu-button"
                  onClick={onOpenPDFReportClick}
                  disabled={isInitialState}
                >
                  {isReportOpening ? (
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  ) : (
                    "Open PDF report"
                  )}
                </button>
              </div>
              <div className="col-md">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-block menu-button"
                  onClick={onNextPatientClick}
                >
                  Next patient
                </button>
              </div>
            </section>
          </React.Fragment>
        )}
        {/* Source */}
        <section className="footer">
          <hr />
          <footer className="row">
            <div className="col">
              <p className="text-justify">
                <small>
                  <b>Source: </b>
                  <a
                    href="https://www.thelancet.com/journals/lanonc/article/PIIS1470-2045(21)00274-6/fulltext"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Gafita, Andrei, Jeremie Calais, Tristan R. Grogan, Boris Hadaschik, Hui Wang, Manuel Weber, Shahneen
                    Sandhu et al. &#34;Nomograms to predict outcomes after 177Lu-PSMA therapy in men with metastatic
                    castration-resistant prostate cancer: an international, multicentre, retrospective study.&#34;&nbsp;
                    <i>The Lancet Oncology</i>&nbsp; (2021).
                  </a>
                </small>
              </p>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}
